import React, { useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import {
  useMedicationFormState,
  handleSubmit,
  therapyLevelOptions,
  MedicationForm,
  MedicationEditMode,
  useMedicationDose,
} from "@/lib/medicationsData";
import { Controller } from "react-hook-form";
import SearchableDropdown from "@/components/AddPrescriptionItem/SearchableDropdown";
import {
  getDrugBySearch,
  setselectedDrugs,
  setDrugSummary,
} from "@/dispatchers/nmsDispatchers";

interface MedicationsModalProps {
  trigger: React.ReactNode;
  onSave: (medication: MedicationForm) => void;
  editMode?: MedicationEditMode;
  isDarkMode?: boolean;
}

export function DashboardMedicationsModal({
  trigger,
  onSave,
  editMode,
  isDarkMode = false,
}: MedicationsModalProps) {
  const {
    open,
    setOpen,
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useMedicationFormState(editMode);

  const { handleDose, drugSearch, drugSummary } = useMedicationDose();

  const therapyLevel = watch("therapyLevel");

  const hasFetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !editMode?.item) return;

    if (hasFetchedRef.current === editMode.item) return;
    hasFetchedRef.current = editMode.item;
  }, [open, editMode?.item, setValue]);
  const handleDrugSearch = async (keyword: string) => {
    await drugSearch(keyword);
  };
  const handleDoseChange = (field, value) => {
    var dose = handleDose(value);
    setValue(field, dose);
  };

  const onSubmit = (data: MedicationForm) => {
    handleSubmit(data, onSave, setOpen, reset);
    hasFetchedRef.current = null; // reset ref on submit for next edit
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      <DialogContent
        className={`sm:max-w-[500px] max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {editMode ? "Edit Patient Medication" : "Add Patient Medication"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleFormSubmit(onSubmit)} className="space-y-6">
          {/* Item */}
          <div className="space-y-2">
            <Label
              htmlFor="item"
              className={`text-sm font-medium ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Item *
            </Label>

            <Controller
              name="item"
              control={control}
              render={({ field }) => (
                <SearchableDropdown
                  className="searchable-dropdown"
                  searchType="drug"
                  placeholder="e.g., Amlodipine 10mg tablets"
                  selectedVal={field.value}
                  handleChange={(val) => {
                    if (val && typeof val === "object") {
                      field.onChange(val?.drugName?.trim() ?? "");
                    } else {
                      field.onChange(val);
                    }
                  }}
                  onInputChange={handleDrugSearch}
                  options={[]} // SearchableDropdown manages fetching
                />
              )}
            />

            {errors.item && (
              <p className="text-sm text-red-600">{errors.item.message}</p>
            )}
          </div>

          {/* Dose */}
          <div className="space-y-2">
            <Label
              htmlFor="dose"
              className={`text-sm font-medium ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Dose *
            </Label>
            <Input
              id="dose"
              type="text"
              {...register("dose")}
              onChange={(e) => handleDoseChange("dose", e.target.value)}
              placeholder="e.g., Take one daily with food"
            />
            {errors.dose && (
              <p className="text-sm text-red-600">{errors.dose.message}</p>
            )}
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <Label
              htmlFor="quantity"
              className={`text-sm font-medium ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Quantity
            </Label>
            <Input
              id="quantity"
              type="text"
              placeholder="e.g., 30"
              {...register("quantity")}
            />
          </div>

          {/* Therapy Level */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              Therapy Level *
            </Label>
            <Select
              value={therapyLevel}
              onValueChange={(value) => setValue("therapyLevel", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {therapyLevelOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label
              htmlFor="notes"
              className="text-sm font-medium text-gray-700"
            >
              Additional Notes
            </Label>
            <Textarea
              id="notes"
              placeholder="Any additional information about this medication..."
              {...register("notes")}
              rows={3}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
                hasFetchedRef.current = null; // reset when closing
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-slate-900 hover:bg-slate-800"
            >
              {editMode ? "Update Medication" : "Add Medication"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AddMedicationButton({
  onSave,
  editMode,
  trigger,
}: {
  onSave: (medication: MedicationForm) => void;
  editMode?: MedicationEditMode;
  trigger?: React.ReactNode;
}) {
  return (
    <DashboardMedicationsModal
      trigger={
        trigger || (
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-500 hover:text-gray-700 p-0 h-auto font-normal"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Medication
          </Button>
        )
      }
      onSave={onSave}
      editMode={editMode}
    />
  );
}
